import { create } from "zustand";

import type {
  DeploymentPlan,
  DeploymentStatus,
  EventEnvelope,
  FitAssessment,
  ParametersSchemaDoc,
  RecommendationReadyPayload,
  SessionState,
  SessionStatus,
} from "@/lib/api/types";

/**
 * Per-session reactive state.
 *
 * Two sources update this store:
 *   1. A `getSession()` snapshot at mount time (so page refresh works).
 *   2. The SSE stream, event-by-event.
 *
 * The Timeline component subscribes to `events`; the sub-views subscribe
 * to the typed slices (`recommendation`, `parametersSchema`, `plan`,
 * `finalStatus`, `error`).
 */

export interface SessionSlice {
  sessionId: string | null;
  state: SessionState | "idle";
  fits: Record<string, FitAssessment>;
  chosenPattern: string | null;
  recommendation: RecommendationReadyPayload | null;
  parametersSchema: ParametersSchemaDoc | null;
  plan: DeploymentPlan | null;
  finalStatus: DeploymentStatus | null;
  scanFailures: Record<string, string>;
  error: string | null;
  events: EventEnvelope[];

  reset: (sessionId: string | null) => void;
  hydrate: (snapshot: SessionStatus) => void;
  consumeEvent: (envelope: EventEnvelope) => void;
}

export const useSessionStore = create<SessionSlice>((set) => ({
  sessionId: null,
  state: "idle",
  fits: {},
  chosenPattern: null,
  recommendation: null,
  parametersSchema: null,
  plan: null,
  finalStatus: null,
  scanFailures: {},
  error: null,
  events: [],

  reset: (sessionId) =>
    set({
      sessionId,
      state: "idle",
      fits: {},
      chosenPattern: null,
      recommendation: null,
      parametersSchema: null,
      plan: null,
      finalStatus: null,
      scanFailures: {},
      error: null,
      events: [],
    }),

  hydrate: (snapshot) =>
    set({
      sessionId: snapshot.session_id,
      state: snapshot.state,
      fits: snapshot.fits,
      chosenPattern: snapshot.chosen_pattern,
      plan: snapshot.plan,
      finalStatus: snapshot.final_status,
      scanFailures: snapshot.scan_failures,
      error: snapshot.error,
    }),

  consumeEvent: (envelope) =>
    set((prev) => {
      const next: Partial<SessionSlice> = {
        events: [...prev.events, envelope],
        state: envelope.state,
      };
      switch (envelope.type) {
        case "recommendation.ready": {
          const payload = envelope.payload as unknown as RecommendationReadyPayload;
          next.recommendation = payload;
          next.fits = payload.fits;
          next.chosenPattern = payload.winner;
          break;
        }
        case "parameters.requested": {
          const payload = envelope.payload as unknown as {
            pattern_name: string;
            schema: ParametersSchemaDoc;
          };
          next.parametersSchema = payload.schema;
          break;
        }
        case "plan.ready": {
          const payload = envelope.payload as unknown as { plan: DeploymentPlan };
          next.plan = payload.plan;
          break;
        }
        case "session.terminal": {
          const payload = envelope.payload as unknown as {
            chosen_pattern: string | null;
            final_status: DeploymentStatus | null;
          };
          next.chosenPattern = payload.chosen_pattern;
          next.finalStatus = payload.final_status;
          break;
        }
        case "error": {
          const payload = envelope.payload as unknown as { error: string };
          next.error = payload.error;
          break;
        }
        default:
          break;
      }
      return next;
    }),
}));
